import { getConfig, getToken, parseToken, readConfig, safe, setConfig, state } from './config'
import { forgetVisit, recentTrigger, registerVisitStart } from './correlation'
import { resetErrors } from './errors'
import {
  checkAbandon,
  markFilterSucceeded,
  markServerErrors,
  markSubmitStarted,
  markSubmitSucceeded,
  resetForms,
  touchedCount,
} from './forms'
import { notifyEffect, settleClicks } from './interactions'
import {
  claimJourney,
  currentJourney,
  isActive,
  isIdle,
  journeyId,
  nextPageSeq,
  pageSeq,
  pushPath,
  replacePath,
  resumeChannel,
  resumeJourney,
  rotateJourney,
  setJourneyUser,
  suspendChannel,
  traversePath,
} from './journey'
import { lookupToasters, toastSeen } from './overlays'
import {
  emitPerfLoad,
  navigationType,
  noteVisibility,
  schedulePerfLoad,
  startPageMetrics,
  takePageMetrics,
} from './performance'
import { clampInt, cleanStr, maskText, normalizeKey, normalizeKeys, scrubPath } from './scrub'
import {
  adoptToken,
  discardJourney,
  flush,
  holdFlush,
  record,
  rekeyJourney,
  release,
  releaseBeforeConfig,
  reserve,
  restoreOutbox,
  snapshot,
  useEndpoint,
} from './transport'
import { router } from '@inertiajs/vue3'

const FLASH_KEYS = ['success', 'warning', 'error', 'info', 'modal_success', 'modal_error']
const FLASH_WINDOW_MS = 1500
const RENDER_TIMEOUT_MS = 1000
const DUPLICATE_CHECK_MS = 300
const PREFETCH_TTL_MS = 60000
const DAY_MS = 86400000
const NO_FLASH_KINDS = new Set(['partial', 'deferred', 'prefetch'])
const CONNECTIONS = new Set(['slow-2g', '2g', '3g', '4g'])

const visits = new Map()
const flashChecked = new Set()

let prefetches = []
let lastPage = null
let lastDwell = null
let navIndex = null
let installAll = null

function pathOf(url) {
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return null
  }
}

function pageInfo(page) {
  return {
    component: page?.component ?? null,
    routeName: page?.props?.routeName ?? null,
    path: scrubPath(page?.url),
  }
}

function queryKeys(url) {
  try {
    return normalizeKeys(new URL(url, window.location.origin).searchParams.keys(), 20)
  } catch {
    return []
  }
}

// A back/forward full load restores page props from history; the document's own props carry the fresh token.
function documentConfig() {
  try {
    const script = document.querySelector('script[data-page="app"][type="application/json"]')

    return script?.textContent ? readConfig(JSON.parse(script.textContent)) : null
  } catch {
    return null
  }
}

function newerToken(config, current) {
  const parsed = parseToken(config?.token)

  if (!parsed) {
    return null
  }

  if (!current || parsed.exp > current.exp) {
    return parsed
  }

  return parsed.exp === current.exp && parsed.user !== current.user ? parsed : null
}

function whenPainted(callback, timeout) {
  let done = false
  const finish = safe((painted) => {
    if (!done) {
      done = true
      callback(painted)
    }
  })

  window.requestAnimationFrame(() => window.requestAnimationFrame(() => finish(true)))
  window.setTimeout(() => finish(false), timeout)
}

function classify(visit) {
  if (visit.prefetch) {
    return 'prefetch'
  }

  if (visit.deferredProps) {
    return 'deferred'
  }

  if (visit.only?.length || visit.except?.length || visit.reset?.length) {
    return 'partial'
  }

  if (visit.headers?.['Cache-Control'] === 'no-cache') {
    return 'reload'
  }

  if (String(visit.method).toLowerCase() !== 'get') {
    return 'submit'
  }

  return visit.replace && visit.preserveState ? 'filter' : 'visit'
}

// Top-level keys and whether files are attached; values are never read.
function describeData(data) {
  const keys = []
  let files = false
  const isFile = (value) => typeof Blob !== 'undefined' && value instanceof Blob
  const hasFile = (value, depth) =>
    isFile(value) ||
    (depth > 0 &&
      value !== null &&
      typeof value === 'object' &&
      Object.values(value).some((item) => hasFile(item, depth - 1)))

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    for (const [key, value] of data.entries()) {
      keys.push(key.split('[')[0])
      files = files || isFile(value)
    }
  } else if (data && typeof data === 'object') {
    for (const [key, value] of Object.entries(data)) {
      keys.push(key)
      files = files || hasFile(value, 2)
    }
  }

  return { keys: normalizeKeys(keys, 30), files }
}

function firstMessages(errors) {
  const messages = {}

  for (const [field, value] of Object.entries(errors ?? {})) {
    const key = normalizeKey(field)
    const message = maskText(Array.isArray(value) ? value[0] : value, 140)

    if (key && message && !(key in messages)) {
      messages[key] = message
    }

    if (Object.keys(messages).length >= 5) {
      break
    }
  }

  return messages
}

function emitNav(entry, outcomeOverride) {
  if (entry.emitted) {
    return
  }

  entry.emitted = true
  visits.delete(entry.id)
  forgetVisit(entry)

  const outcome = outcomeOverride ?? entry.outcome ?? 'success'

  // Server rows already cover prefetches and successful deferred loads.
  if (entry.kind === 'prefetch' || (entry.kind === 'deferred' && outcome === 'success')) {
    release(entry.slot)

    return
  }

  record(
    'nav.request',
    {
      target: `${entry.method.toUpperCase()} ${scrubPath(entry.href) ?? '/'}`,
      status: entry.status,
      duration_ms: (entry.finishedAt ?? Date.now()) - entry.start,
      correlation_id: entry.correlationId,
      props: {
        ...entry.props,
        outcome,
        error_keys: entry.errorKeys,
        errors: entry.errors,
        to_component: cleanStr(entry.toComponent, 128),
        to_route: cleanStr(entry.toRoute, 128),
        render_ms: clampInt(entry.renderMs, 0, 600000),
      },
    },
    entry.slot
  )
}

function newestPendingVisit() {
  let newest = null

  visits.forEach((entry) => {
    if (!entry.finishedAt && entry.kind !== 'prefetch') {
      newest = entry
    }
  })

  return newest
}

function abandonVisits() {
  Array.from(visits.values()).forEach((entry) => {
    if (entry.kind !== 'prefetch') {
      emitNav(entry, entry.finishedAt ? undefined : 'abandoned')
    }
  })
}

function bodyContains(probe) {
  return (document.body?.textContent ?? '').replace(/\s+/g, ' ').includes(probe)
}

// shown: a toast with the same text within 1.5 s, or the text is on the page at the next frame or at +1.5 s.
function checkFlash(page, key) {
  if (!isActive() || !key || flashChecked.has(key)) {
    return
  }

  flashChecked.add(key)

  if (flashChecked.size > 50) {
    flashChecked.delete(flashChecked.values().next().value)
  }

  const flash = page?.props?.flash

  if (!flash || typeof flash !== 'object') {
    return
  }

  FLASH_KEYS.forEach((name) => {
    const text = typeof flash[name] === 'string' ? flash[name].replace(/\s+/g, ' ').trim() : ''
    const slot = text === '' ? null : reserve()

    if (!slot) {
      return
    }

    const at = Date.now()
    const probe = text.slice(0, 40)
    const emit = (shown) => record('outcome.flash', { target: name, props: { text: maskText(text, 140), shown } }, slot)

    whenPainted(() => {
      if (bodyContains(probe)) {
        emit(true)

        return
      }

      window.setTimeout(
        safe(() => emit(toastSeen(probe, at - FLASH_WINDOW_MS, at + FLASH_WINDOW_MS) || bodyContains(probe))),
        Math.max(FLASH_WINDOW_MS - (Date.now() - at), 0)
      )
    }, FLASH_WINDOW_MS)
  })
}

function referrerInfo() {
  if (!document.referrer) {
    return { kind: 'none' }
  }

  try {
    const url = new URL(document.referrer)

    return url.origin === window.location.origin ? { kind: 'internal' } : { kind: 'external', host: url.host }
  } catch {
    return { kind: 'none' }
  }
}

function timeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return null
  }
}

function recordJourneyStart(start, page, midLoad) {
  const referrer = midLoad ? { kind: 'internal' } : referrerInfo()
  const ui = page?.props?.auth?.backoffice_ui
  const connection = navigator.connection?.effectiveType
  const dpr = Number(window.devicePixelRatio)

  record('journey.start', {
    props: {
      reason: start.reason,
      previous_journey_id: start.previous_journey_id,
      nav_type: navigationType(),
      referrer: referrer.kind,
      referrer_host: cleanStr(referrer.host, 64),
      viewport: [clampInt(window.innerWidth, 0, 20000) ?? 0, clampInt(window.innerHeight, 0, 20000) ?? 0],
      dpr: Number.isFinite(dpr) ? Math.min(Math.max(dpr, 0), 10) : undefined,
      touch: (navigator.maxTouchPoints ?? 0) > 0,
      tz: cleanStr(timeZone(), 64),
      locale: cleanStr(navigator.language, 16),
      ui: ui === 'v1' || ui === 'v2' ? ui : undefined,
      kiosk: page?.props?.courseAppKiosk?.active === true,
      nav_api: 'navigation' in window,
      connection: CONNECTIONS.has(connection) ? connection : undefined,
    },
  })
}

function onDuplicate() {
  if (!isActive()) {
    return
  }

  const fromId = journeyId()
  const fromPageSeq = pageSeq()
  const start = rotateJourney('duplicate')

  if (!isActive()) {
    discardJourney(fromId)

    return
  }

  recordJourneyStart(start, lastPage, false)
  rekeyJourney(fromId, journeyId(), fromPageSeq)
}

function install() {
  state.installed = true
  installAll?.()
  document.addEventListener('visibilitychange', safe(onVisibilityChange))
  window.addEventListener('pagehide', safe(onPageHide))
  window.addEventListener('pageshow', safe(onPageShow))
  holdFlush(DUPLICATE_CHECK_MS)
  claimJourney(safe(onDuplicate))
  restoreOutbox()
}

function endPage(exit) {
  settleClicks(exit === 'close' || exit === 'bfcache' ? 'navigation' : null)

  const dwell = Date.now() - state.pageStartedAt

  lastDwell = dwell
  record('page.leave', {
    target: state.page?.component,
    duration_ms: dwell,
    props: {
      exit,
      ...takePageMetrics(),
      clicks: clampInt(state.stats.clicks, 0, 100000),
      fields_touched: clampInt(touchedCount(), 0, 1000),
      xhr: clampInt(state.stats.xhr, 0, 100000),
    },
  })
  checkAbandon()
  state.pageLeft = true
}

function updateStack(view, path) {
  if (view.stack === 'push') {
    pushPath(path)
  } else if (view.stack === 'replace') {
    replacePath(path)
  } else if (view.stack === 'traverse' && !traversePath(path)) {
    replacePath(path)
  }

  const index = window.navigation?.currentEntry?.index

  navIndex = Number.isInteger(index) ? index : null
}

function beginView(page, previous, view, start) {
  if (!state.installed) {
    install()
  }

  if (start) {
    recordJourneyStart(start, page, !view.boot)
  }

  nextPageSeq()
  state.correlationId = view.correlation_id ?? null
  state.pageStartedAt = Date.now()
  state.pageLeft = false
  state.stats = { clicks: 0, xhr: 0 }
  startPageMetrics()
  resetForms()
  resetErrors()

  record('page.view', {
    target: page?.component,
    correlation_id: state.correlationId,
    props: {
      via: view.via,
      nav_id: cleanStr(view.nav_id, 64),
      trigger_seq: view.trigger_seq,
      from_component: cleanStr(previous?.component, 128),
      from_route: cleanStr(previous?.routeName, 128),
      from_path: previous?.path,
      query_keys: queryKeys(page?.url),
      error_status: page?.component === 'Error' ? clampInt(Number(page.props?.status), 100, 599) : undefined,
      prev_dwell_ms: lastDwell === null ? undefined : clampInt(lastDwell, 0, DAY_MS),
    },
  })

  updateStack(view, pathOf(page?.url))
  lookupToasters()

  if (view.boot) {
    schedulePerfLoad(snapshot())
  }

  if (view.flashKey) {
    checkFlash(page, view.flashKey)
  }
}

function openJourney(userId, rotate) {
  if (!currentJourney()) {
    return resumeJourney(userId)
  }

  if (rotate) {
    return rotateJourney(rotate, userId)
  }

  setJourneyUser(userId)

  return null
}

function adopt(nextConfig, candidate) {
  setConfig(nextConfig)

  if (nextConfig) {
    useEndpoint(nextConfig.endpoint)
  }

  if (candidate) {
    adoptToken(candidate)
  }
}

// A page change, or an identity change, closes the current page view and opens a new one.
function applyPage(page, nextConfig, view, pageChange) {
  const wasActive = isActive()
  const previous = state.page
  const current = getToken()
  const candidate = newerToken(nextConfig, current)
  const userChanged = Boolean(candidate && current && candidate.user !== current.user)
  const identity = userChanged && current.user !== null && candidate.user !== null
  const userId = (candidate ?? current)?.user ?? null

  if (!pageChange && !identity) {
    if (userChanged || (wasActive && !nextConfig)) {
      flush()
    }

    adopt(nextConfig, candidate)
    state.page = page ? pageInfo(page) : state.page

    if (nextConfig && !wasActive) {
      const start = openJourney(userId, null)

      if (isActive()) {
        beginView(page, previous, view, start)
      }
    } else if (candidate) {
      setJourneyUser(userId)
    }

    return
  }

  const rotate = identity ? 'identity' : pageChange && wasActive && (state.idleGap || isIdle()) ? 'idle' : null

  state.idleGap = false

  if (wasActive && !state.pageLeft) {
    endPage(identity ? 'identity' : 'navigate')
  }

  if (userChanged || (wasActive && !nextConfig)) {
    flush()
  }

  adopt(nextConfig, candidate)
  state.page = pageInfo(page)

  if (!nextConfig) {
    state.correlationId = null

    return
  }

  const start = openJourney(userId, rotate)

  if (isActive()) {
    beginView(page, previous, view, start)
  }
}

function navigateView(page, detail, entry) {
  if (detail.cached) {
    const href = new URL(page.url, window.location.origin).href
    const now = Date.now()
    const prefetch = prefetches
      .filter((candidate) => now - candidate.start < PREFETCH_TTL_MS)
      .reverse()
      .find((candidate) => candidate.href === href || pathOf(candidate.href) === pathOf(href))

    return {
      via: 'prefetch_cache',
      nav_id: detail.visitId,
      correlation_id: prefetch?.correlationId ?? null,
      trigger_seq: recentTrigger()?.seq,
      stack: 'push',
      flashKey: detail.visitId,
    }
  }

  if (!detail.visitId) {
    return { via: traverseVia(page), correlation_id: null, stack: 'none', flashKey: null }
  }

  return {
    via: entry && pathOf(page.url) !== entry.path ? 'redirect' : 'visit',
    nav_id: detail.visitId,
    correlation_id: entry?.correlationId ?? null,
    trigger_seq: entry?.trigger?.seq,
    stack: 'push',
    flashKey: entry && NO_FLASH_KINDS.has(entry.kind) ? null : detail.visitId,
  }
}

// Popstate navigates carry no visitId and no direction; history.state is Inertia's (and encrypted), so it is never read.
function traverseVia(page) {
  const index = window.navigation?.currentEntry?.index
  const stackVia = traversePath(pathOf(page.url))

  if (Number.isInteger(index) && navIndex !== null && index !== navIndex) {
    return index < navIndex ? 'back' : 'forward'
  }

  return stackVia ?? 'history'
}

function bootView(detail) {
  const type = navigationType()

  return {
    boot: true,
    via: type === 'reload' ? 'reload' : type === 'back_forward' ? 'history' : 'initial',
    nav_id: detail.visitId,
    stack: type === 'reload' ? 'replace' : type === 'back_forward' ? 'traverse' : 'push',
    flashKey: 'initial',
  }
}

function measureRender(entry) {
  const startedAt = window.performance.now()

  entry.navigated = true
  whenPainted((painted) => {
    entry.renderMs = painted ? window.performance.now() - startedAt : undefined
    entry.rendered = true

    if (entry.finishedAt) {
      emitNav(entry)
    }
  }, RENDER_TIMEOUT_MS)
}

// A GET visit started by submitting a form (search, lookup, report filters) is that form's success, unlike a link inside it.
function isFormGet(entry) {
  return entry.method === 'get' && entry.trigger?.submitted === true && !!entry.trigger.form
}

function onNavigate(detail) {
  const page = detail?.page

  if (!page) {
    return
  }

  const boot = !state.booted
  const entry = detail.visitId ? visits.get(detail.visitId) : null
  const nextConfig =
    boot && navigationType() === 'back_forward' ? (documentConfig() ?? readConfig(page)) : readConfig(page)

  state.booted = true
  lastPage = page

  // A submit that lands on a new page without errors succeeded, before success/finish fire.
  if (entry && Object.keys(page.props?.errors ?? {}).length === 0) {
    if (entry.method !== 'get') {
      markSubmitSucceeded(entry.trigger)
    } else if (isFormGet(entry)) {
      markFilterSucceeded(entry.trigger, entry.start)
    }
  }

  const view = boot ? bootView(detail) : navigateView(page, detail, entry)

  if (boot) {
    view.correlation_id = nextConfig?.correlation_id ?? null
  }

  applyPage(page, nextConfig, view, true)

  if (boot) {
    releaseBeforeConfig()
  }

  if (entry) {
    measureRender(entry)
  }
}

function onResponse(page, visitId) {
  if (!state.booted || !page) {
    return
  }

  lastPage = page

  const entry = visits.get(visitId)

  applyPage(
    page,
    readConfig(page),
    { via: 'visit', nav_id: visitId, correlation_id: entry?.correlationId ?? null, stack: 'none', flashKey: null },
    false
  )

  if (!entry || !NO_FLASH_KINDS.has(entry.kind)) {
    checkFlash(page, visitId)
  }
}

function onBefore(visit) {
  if (!isActive() || !visit || visit.prefetch || visit.deferredProps) {
    return
  }

  state.lastNavigationAt = Date.now()
  notifyEffect('navigation')
}

function onStart(visit) {
  if (!isActive() || !visit?.id) {
    return
  }

  const now = Date.now()
  const kind = classify(visit)
  const url = new URL(visit.url)
  const method = String(visit.method || 'get').toLowerCase()
  const background = kind === 'prefetch' || kind === 'deferred'
  const trigger = background ? null : recentTrigger(now)
  const data = method === 'get' ? null : describeData(visit.data)

  url.hash = ''

  const entry = {
    id: visit.id,
    kind,
    method,
    href: url.href,
    path: url.pathname,
    start: now,
    trigger,
    // Search and filter boxes visit with GET + replace (often partial); polling reloads are async.
    filter: method === 'get' && !background && visit.replace === true && visit.async !== true,
    correlationId: null,
    slot: kind === 'prefetch' ? null : reserve(),
    props: {
      kind,
      nav_id: cleanStr(visit.id, 64),
      trigger_seq: trigger?.seq,
      trigger_label: maskText(trigger?.label, 80),
      only: normalizeKeys(visit.only, 20),
      except: normalizeKeys(visit.except, 20),
      reset: normalizeKeys(visit.reset, 20),
      data_keys: data?.keys,
      has_files: data ? data.files : undefined,
      async: visit.async === true,
    },
    errorBag: visit.errorBag || null,
    status: undefined,
    outcome: null,
    finishedAt: null,
  }

  visits.set(visit.id, entry)
  registerVisitStart(entry)

  if (method !== 'get') {
    markSubmitStarted(trigger)
  }

  if (kind === 'prefetch') {
    prefetches = prefetches.filter((candidate) => now - candidate.start < PREFETCH_TTL_MS).concat(entry)
  }
}

function onSuccess(detail) {
  const entry = visits.get(detail?.visitId)

  if (entry) {
    entry.outcome = entry.outcome ?? 'success'
    entry.status = entry.status ?? 200
    entry.toComponent = detail.page?.component
    entry.toRoute = detail.page?.props?.routeName
  }

  onResponse(detail?.page, detail?.visitId)
}

function onError(detail) {
  const entry = visits.get(detail?.visitId)

  if (entry) {
    entry.outcome = 'validation_error'
    entry.status = entry.status ?? 200
    entry.errorKeys = normalizeKeys(Object.keys(detail.errors ?? {}), 20)
    entry.errors = firstMessages(detail.errors)
    entry.props.error_bag = cleanStr(entry.errorBag, 64)
    entry.toComponent = detail.page?.component
    entry.toRoute = detail.page?.props?.routeName
  }

  if (isActive()) {
    markServerErrors()
  }

  onResponse(detail?.page, detail?.visitId)
}

// httpException and networkError carry no visit id, so they attach to the newest visit still in flight.
function onHttpException(response) {
  const entry = newestPendingVisit()

  if (entry) {
    entry.outcome = 'http_error'
    entry.status = response?.status
  }
}

function onNetworkError() {
  const entry = newestPendingVisit()

  if (entry) {
    entry.outcome = 'network_error'
  }
}

function onFinish(visit) {
  const entry = visits.get(visit?.id)

  if (!entry || entry.emitted) {
    return
  }

  entry.finishedAt = Date.now()

  if (visit.cancelled) {
    entry.outcome = 'cancelled'
  } else if (visit.interrupted) {
    entry.outcome = 'interrupted'
  } else {
    entry.outcome = entry.outcome ?? 'success'
  }

  if (entry.outcome === 'success' && entry.method !== 'get') {
    markSubmitSucceeded(entry.trigger)
  } else if (entry.outcome === 'success' && (entry.filter || isFormGet(entry))) {
    markFilterSucceeded(entry.trigger, entry.start)
  }

  if (!entry.navigated || entry.rendered) {
    emitNav(entry)
  }
}

function onVisibilityChange() {
  noteVisibility()

  if (document.visibilityState === 'hidden') {
    emitPerfLoad()
    flush()
  }
}

function onPageHide(event) {
  if (isActive() && !state.pageLeft) {
    endPage(event.persisted ? 'bfcache' : 'close')
    abandonVisits()
  }

  flush({ beacon: true })

  if (event.persisted) {
    suspendChannel()
  }
}

function onPageShow(event) {
  if (!event.persisted) {
    return
  }

  resumeChannel()
  restoreOutbox()

  if (isActive() && state.pageLeft && lastPage) {
    applyPage(lastPage, getConfig(), { via: 'bfcache', correlation_id: state.correlationId, stack: 'none' }, true)
  }
}

export function registerRouterListeners(onInstall) {
  installAll = onInstall

  router.on(
    'before',
    safe((event) => {
      onBefore(event.detail?.visit)
    })
  )
  router.on(
    'start',
    safe((event) => {
      onStart(event.detail?.visit)
    })
  )
  router.on(
    'navigate',
    safe((event) => {
      onNavigate(event.detail)
    })
  )
  router.on(
    'success',
    safe((event) => {
      onSuccess(event.detail)
    })
  )
  router.on(
    'error',
    safe((event) => {
      onError(event.detail)
    })
  )
  router.on(
    'httpException',
    safe((event) => {
      onHttpException(event.detail?.response)
    })
  )
  router.on(
    'networkError',
    safe(() => {
      onNetworkError()
    })
  )
  router.on(
    'finish',
    safe((event) => {
      onFinish(event.detail?.visit)
    })
  )
}
